import {
  LEAD_DESTINATION_KINDS,
  LEAD_DESTINATION_LABELS,
  LEAD_QUESTION_LABELS,
  LEAD_QUESTION_TYPES,
  LEAD_STATUSES,
  LEAD_STATUS_LABELS,
  META_LEADS_SCOPES,
  roleHas,
  type CreateLeadDestinationInput,
  type CreateLeadFormInput,
  type LeadDestinationDto,
  type LeadDestinationKind,
  type LeadDto,
  type LeadFormDto,
  type LeadQuestionType,
  type LeadStatus,
  type LeadsOverviewDto,
} from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Download,
  ExternalLink,
  FileText,
  Inbox,
  MessageCircle,
  Plus,
  RefreshCw,
  Send,
  Settings2,
  Trash2,
  Users,
  Zap,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ClientFilter } from "@/components/client-filter";
import { SecretBox } from "@/components/secret-box";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { FormField } from "@/components/form-field";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { errorMessage } from "@/lib/api";
import { api7, leadsKey, queries7 } from "@/lib/fase7-api";
import { queries } from "@/lib/queries";
import { formatDate, formatRelative } from "@/lib/utils";


interface LeadsSearch {
  aba?: "leads" | "formularios" | "destinos";
  lead?: string;
}

export const Route = createFileRoute("/app/$workspaceId/leads")({
  validateSearch: (raw: Record<string, unknown>): LeadsSearch => {
    const out: LeadsSearch = {};
    if (raw.aba === "leads" || raw.aba === "formularios" || raw.aba === "destinos") out.aba = raw.aba;
    if (typeof raw.lead === "string" && raw.lead) out.lead = raw.lead;
    return out;
  },
  component: LeadsPage,
});

const ALL = "__todos__";

function LeadsPage() {
  const { workspaceId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const workspace = useQuery(queries.workspace(workspaceId));
  const metaStatus = useQuery(queries.metaStatus(workspaceId));
  const canWrite = workspace.data ? roleHas(workspace.data.role, "leads:write") : false;
  const canManage = workspace.data ? roleHas(workspace.data.role, "alerts:manage") : false;
  const [aba, setAba] = useState<string>(search.aba ?? "leads");
  const [openLead, setOpenLead] = useState<string>(search.lead ?? "");
  const queryClient = useQueryClient();
  const mock = useMutation({
    mutationFn: () => api7.mockLead(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: leadsKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "notifications"] });
      toast.success("Lead simulado disparado pelo webhook assinado.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <>
      <PageHeader
        title="Leads"
        description="Formulários instantâneos da Meta: leads em tempo real pelo webhook, com envio para webhook do cliente, e-mail, Google Sheets e WhatsApp."
        actions={
          metaStatus.data?.mode === "mock" && canWrite ? (
            <Button variant="outline" onClick={() => mock.mutate()} loading={mock.isPending}>
              <Zap /> Disparar lead simulado
            </Button>
          ) : null
        }
      />
      <Tabs value={aba} onValueChange={setAba}>
        <TabsList>
          <TabsTrigger value="leads"><Inbox className="size-4" /> Leads</TabsTrigger>
          <TabsTrigger value="formularios"><FileText className="size-4" /> Formulários</TabsTrigger>
          <TabsTrigger value="destinos"><Send className="size-4" /> Destinos</TabsTrigger>
        </TabsList>
        <TabsContent value="leads"><LeadsTab workspaceId={workspaceId} canWrite={canWrite} onOpen={setOpenLead} /></TabsContent>
        <TabsContent value="formularios"><FormsTab workspaceId={workspaceId} canWrite={canWrite} canManage={canManage} /></TabsContent>
        <TabsContent value="destinos"><DestinationsTab workspaceId={workspaceId} canWrite={canWrite} /></TabsContent>
      </Tabs>
      <LeadDialog
        workspaceId={workspaceId}
        leadId={openLead}
        canWrite={canWrite}
        onOpenChange={(o) => {
          if (!o) {
            setOpenLead("");
            if (search.lead) void navigate({ search: search.aba ? { aba: search.aba } : {} });
          }
        }}
      />
    </>
  );
}

// ---- Leads ---------------------------------------------------------------------------------------

function LeadsTab({ workspaceId, canWrite, onOpen }: { workspaceId: string; canWrite: boolean; onOpen: (id: string) => void }) {
  const overview = useQuery(queries7.leadsOverview(workspaceId));
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  const [form, setForm] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const params = useMemo(
    () => ({ clientId, formMetaId: form === ALL ? undefined : form, status: status === ALL ? undefined : status, q: q || undefined, from: from || undefined, to: to || undefined, limit: "100" }),
    [clientId, form, status, q, from, to],
  );
  const leads = useQuery(queries7.leads(workspaceId, params));
  const exportar = useMutation({
    mutationFn: (format: "csv" | "xlsx") => api7.exportLeads(workspaceId, params, format),
    onSuccess: (name) => toast.success(`Arquivo ${name} baixado.`),
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <ClientFilter workspaceId={workspaceId} value={clientId} onChange={setClientId} />
        <Select value={form} onValueChange={setForm}>
          <SelectTrigger className="sm:w-56" aria-label="Formulário"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os formulários</SelectItem>
            {overview.data?.forms.map((f) => (
              <SelectItem key={f.metaId} value={f.metaId}>{f.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="sm:w-40" aria-label="Status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os status</SelectItem>
            {LEAD_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{LEAD_STATUS_LABELS[s]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input type="date" aria-label="De" value={from} onChange={(e) => setFrom(e.target.value)} className="sm:w-40" />
        <Input type="date" aria-label="Até" value={to} onChange={(e) => setTo(e.target.value)} className="sm:w-40" />
        <Input placeholder="Buscar nome, e-mail, telefone…" value={q} onChange={(e) => setQ(e.target.value)} className="sm:w-56" />
        {canWrite ? (
          <div className="flex gap-2 sm:ml-auto">
            <Button variant="outline" onClick={() => exportar.mutate("csv")} loading={exportar.isPending}><Download /> CSV</Button>
            <Button variant="outline" onClick={() => exportar.mutate("xlsx")} loading={exportar.isPending}><Download /> XLSX</Button>
          </div>
        ) : null}
      </div>
      <Card>
        <CardContent>
          {leads.isPending ? (
            <LoadingState rows={4} />
          ) : leads.isError ? (
            <ErrorState error={leads.error} onRetry={() => void leads.refetch()} />
          ) : leads.data.items.length === 0 ? (
            <EmptyState icon={<Inbox className="size-8" aria-hidden />} title="Nenhum lead" description="Assine o webhook da página na aba Formulários (ou faça o backfill dos últimos 90 dias) para os leads aparecerem aqui." />
          ) : (
            <>
              <p className="mb-2 text-xs text-muted-foreground">{leads.data.total} lead(s)</p>
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Quando</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Contato</TableHead>
                      <TableHead>Formulário / origem</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Envios</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.data.items.map((l) => (
                      <TableRow key={l.id} className="cursor-pointer" onClick={() => onOpen(l.id)}>
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground" title={formatDate(l.metaCreatedAt)}>{formatRelative(l.metaCreatedAt)}</TableCell>
                        <TableCell className="font-medium">{l.name ?? "—"}</TableCell>
                        <TableCell className="text-sm">
                          <p>{l.email ?? "—"}</p>
                          <p className="flex items-center gap-1 text-muted-foreground">
                            {l.phone ?? "—"}
                            {l.whatsappUrl ? (
                              <a href={l.whatsappUrl} target="_blank" rel="noreferrer" aria-label="Abrir no WhatsApp" onClick={(e) => e.stopPropagation()} className="text-emerald-600">
                                <MessageCircle className="size-4" />
                              </a>
                            ) : null}
                          </p>
                        </TableCell>
                        <TableCell className="text-sm">
                          <p>{l.formName ?? l.formMetaId}</p>
                          <p className="text-xs text-muted-foreground">{l.isOrganic ? "orgânico" : (l.campaignName ?? l.campaignMetaId ?? "—")}</p>
                        </TableCell>
                        <TableCell><Badge variant={l.status === "convertido" ? "success" : l.status === "descartado" ? "secondary" : "outline"}>{LEAD_STATUS_LABELS[l.status]}</Badge></TableCell>
                        <TableCell className="text-xs">
                          {l.deliveries.length === 0 ? "—" : `${l.deliveries.filter((d) => d.status === "ok").length}/${l.deliveries.length} ok`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function LeadDialog({ workspaceId, leadId, canWrite, onOpenChange }: { workspaceId: string; leadId: string; canWrite: boolean; onOpenChange: (o: boolean) => void }) {
  const queryClient = useQueryClient();
  const lead = useQuery(queries7.lead(workspaceId, leadId));
  const destinations = useQuery(queries7.leadDestinations(workspaceId));
  const invalidate = () => queryClient.invalidateQueries({ queryKey: leadsKey(workspaceId) });
  const update = useMutation({
    mutationFn: (body: { status?: LeadStatus; notes?: string }) => api7.updateLead(workspaceId, leadId, body),
    onSuccess: async () => {
      await invalidate();
      toast.success("Lead atualizado.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const resend = useMutation({
    mutationFn: (destinationId: string) => api7.resendLead(workspaceId, leadId, destinationId),
    onSuccess: () => invalidate(),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const l: LeadDto | undefined = lead.data;
  return (
    <Dialog open={leadId.length > 0} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{l?.name ?? "Lead"}</DialogTitle>
          <DialogDescription>{l ? `${l.formName ?? l.formMetaId} · ${l.pageName ?? l.pageMetaId} · ${formatDate(l.metaCreatedAt)}` : null}</DialogDescription>
        </DialogHeader>
        {lead.isPending ? (
          <LoadingState rows={3} />
        ) : lead.isError ? (
          <ErrorState error={lead.error} />
        ) : l ? (
          <div className="space-y-4">
            <div className="grid gap-2 text-sm sm:grid-cols-2">
              <p><span className="text-muted-foreground">E-mail: </span>{l.email ?? "—"}</p>
              <p className="flex items-center gap-2">
                <span className="text-muted-foreground">Telefone: </span>{l.phone ?? "—"}
                {l.whatsappUrl ? (
                  <Button asChild size="sm" variant="outline">
                    <a href={l.whatsappUrl} target="_blank" rel="noreferrer"><MessageCircle /> WhatsApp</a>
                  </Button>
                ) : null}
              </p>
              <p><span className="text-muted-foreground">Campanha: </span>{l.campaignName ?? l.campaignMetaId ?? "—"}</p>
              <p><span className="text-muted-foreground">Anúncio: </span>{l.adName ?? l.adMetaId ?? (l.isOrganic ? "orgânico" : "—")}</p>
              <p><span className="text-muted-foreground">Origem: </span>{l.source} · {l.platform ?? "—"}</p>
              <p><span className="text-muted-foreground">Id na Meta: </span>{l.metaId}</p>
            </div>
            <div className="rounded-md border p-3">
              <p className="mb-1 text-xs font-medium text-muted-foreground">Respostas</p>
              <dl className="grid gap-1 text-sm sm:grid-cols-2">
                {Object.entries(l.fields).map(([k, v]) => (
                  <div key={k}><dt className="text-xs text-muted-foreground">{k}</dt><dd>{v}</dd></div>
                ))}
              </dl>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField id="lead-status" label="Status do contato">
                <Select value={l.status} disabled={!canWrite} onValueChange={(v) => update.mutate({ status: v as LeadStatus })}>
                  <SelectTrigger id="lead-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{LEAD_STATUS_LABELS[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <NotesField key={`${l.id}:${l.notes}`} initial={l.notes} disabled={!canWrite} onSave={(notes) => update.mutate({ notes })} />
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">Envios aos destinos</p>
              {l.deliveries.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum destino casou com este lead.</p> : null}
              <ul className="divide-y text-sm">
                {l.deliveries.map((d) => (
                  <li key={d.id} className="flex flex-wrap items-center gap-2 py-2">
                    <Badge variant={d.status === "ok" ? "success" : d.status === "erro" ? "destructive" : "warning"}>{d.status}</Badge>
                    <span className="font-medium">{d.destinationName}</span>
                    <span className="text-xs text-muted-foreground">{LEAD_DESTINATION_LABELS[d.kind]} · {d.attempts} tentativa(s){d.error ? ` · ${d.error}` : ""}</span>
                    {canWrite ? (
                      <Button size="sm" variant="ghost" className="ml-auto" onClick={() => resend.mutate(d.destinationId)} loading={resend.isPending && resend.variables === d.destinationId}>
                        <RefreshCw /> Reenviar
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
              {canWrite && destinations.data?.some((d) => !l.deliveries.some((x) => x.destinationId === d.id)) ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {destinations.data
                    .filter((d) => !l.deliveries.some((x) => x.destinationId === d.id))
                    .map((d) => (
                      <Button key={d.id} size="sm" variant="outline" onClick={() => resend.mutate(d.id)}>
                        <Send /> Enviar para {d.name}
                      </Button>
                    ))}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function NotesField({ initial, disabled, onSave }: { initial: string; disabled: boolean; onSave: (notes: string) => void }) {
  const [notes, setNotes] = useState(initial);
  return (
    <FormField id="lead-notes" label="Observações">
      <Textarea id="lead-notes" value={notes} disabled={disabled} onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== initial && onSave(notes)} />
    </FormField>
  );
}

// ---- Formulários ----------------------------------------------------------------------------------

function FormsTab({ workspaceId, canWrite, canManage }: { workspaceId: string; canWrite: boolean; canManage: boolean }) {
  const queryClient = useQueryClient();
  const overview = useQuery(queries7.leadsOverview(workspaceId));
  const [novo, setNovo] = useState<string>("");
  const invalidate = () => queryClient.invalidateQueries({ queryKey: leadsKey(workspaceId) });
  const sync = useMutation({
    mutationFn: (pageId: string) => api7.syncForms(workspaceId, pageId),
    onSuccess: async (forms) => {
      await invalidate();
      toast.success(`${forms.length} formulário(s) sincronizado(s).`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const subscribe = useMutation({
    mutationFn: (pageId: string) => api7.subscribePage(workspaceId, pageId),
    onSuccess: async (p) => {
      await invalidate();
      if (p.leadgenSubscriptionError) toast.error(p.leadgenSubscriptionError);
      else toast.success("Recebimento em tempo real ativado na página.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const backfill = useMutation({
    mutationFn: (formMetaId: string) => api7.backfill(workspaceId, formMetaId, 90),
    onSuccess: async (r) => {
      await invalidate();
      toast.success(`${r.fetched} lead(s) lidos na Meta, ${r.created} novo(s) gravado(s).`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const [retention, setRetention] = useState<string>("");
  const saveRetention = useMutation({
    mutationFn: (days: number) => api7.saveLeadsSettings(workspaceId, days),
    onSuccess: async () => {
      await invalidate();
      toast.success("Retenção atualizada.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  if (overview.isPending) return <LoadingState rows={3} />;
  if (overview.isError) return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;
  const o: LeadsOverviewDto = overview.data;
  return (
    <div className="space-y-4">
      {!o.webhookConfigured ? (
        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
          O webhook da Meta ainda não está configurado (META_WEBHOOK_VERIFY_TOKEN e assinatura do campo leadgen no app). Veja o README, seção "Leads".
        </p>
      ) : null}
      <Card>
        <CardHeader>
          <CardTitle>Páginas</CardTitle>
          <CardDescription>Ative o recebimento em tempo real (assinatura <code>leadgen</code>) e sincronize os formulários de cada página. Exige as permissões {META_LEADS_SCOPES.join(" e ")} na conexão.</CardDescription>
        </CardHeader>
        <CardContent>
          {o.pages.length === 0 ? <EmptyState title="Nenhuma página" description="Conecte uma conta Meta com páginas em Integrações Meta." /> : null}
          <ul className="divide-y">
            {o.pages.map((p) => (
              <li key={p.metaId} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium">{p.name}</p>
                    {p.leadgenSubscribedAt ? <Badge variant="success">tempo real ativo</Badge> : <Badge variant="secondary">sem assinatura</Badge>}
                    {!p.scopesOk ? <Badge variant="warning">faltam permissões: {p.missingScopes.join(", ")}</Badge> : null}
                  </div>
                  <p className="text-xs text-muted-foreground">{p.metaId}{p.leadgenSubscriptionError ? ` · ${p.leadgenSubscriptionError}` : ""}</p>
                </div>
                {canWrite ? (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => sync.mutate(p.metaId)} loading={sync.isPending && sync.variables === p.metaId}><RefreshCw /> Sincronizar formulários</Button>
                    {!p.leadgenSubscribedAt ? (
                      <Button size="sm" onClick={() => subscribe.mutate(p.metaId)} loading={subscribe.isPending && subscribe.variables === p.metaId}>Ativar tempo real</Button>
                    ) : null}
                    <Button size="sm" variant="outline" onClick={() => setNovo(p.metaId)}><Plus /> Criar formulário</Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Formulários</CardTitle>
          <CardDescription>Espelho dos formulários das páginas. "Buscar 90 dias" lê na Meta os leads antigos do formulário (a Meta guarda só 90 dias).</CardDescription>
        </CardHeader>
        <CardContent>
          {o.forms.length === 0 ? <EmptyState icon={<FileText className="size-8" aria-hidden />} title="Nenhum formulário" description="Sincronize os formulários de uma página ou crie um novo." /> : null}
          <ul className="divide-y">
            {o.forms.map((f) => (
              <li key={f.metaId} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium">{f.name}</p>
                    <Badge variant={f.status === "ACTIVE" ? "success" : "secondary"}>{f.status}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {f.pageName ?? f.pageMetaId} · {f.questions.map((q) => q.label).join(", ")} · {f.leadsCount ?? "?"} na Meta · {f.localLeads} aqui
                    {f.privacyPolicyUrl ? (
                      <>
                        {" · "}
                        <a href={f.privacyPolicyUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 underline">política <ExternalLink className="size-3" /></a>
                      </>
                    ) : null}
                  </p>
                </div>
                {canWrite ? (
                  <Button size="sm" variant="outline" onClick={() => backfill.mutate(f.metaId)} loading={backfill.isPending && backfill.variables === f.metaId}><Download /> Buscar 90 dias</Button>
                ) : null}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
      {canManage ? (
        <Card>
          <CardHeader>
            <CardTitle><Settings2 className="mr-1 inline size-4" /> Retenção dos leads (LGPD)</CardTitle>
            <CardDescription>Leads mais antigos que isso são apagados automaticamente pelo job de limpeza (a cada hora). Hoje: {o.settings.retentionDays} dias.</CardDescription>
          </CardHeader>
          <CardContent className="flex items-end gap-2">
            <FormField id="retencao" label="Dias (30 a 3650)">
              <Input id="retencao" type="number" min={30} max={3650} value={retention || String(o.settings.retentionDays)} onChange={(e) => setRetention(e.target.value)} className="w-40" />
            </FormField>
            <Button onClick={() => saveRetention.mutate(Number(retention || o.settings.retentionDays))} loading={saveRetention.isPending}>Salvar</Button>
          </CardContent>
        </Card>
      ) : null}
      <CreateFormDialog workspaceId={workspaceId} pageId={novo} onOpenChange={(open) => !open && setNovo("")} />
    </div>
  );
}

function CreateFormDialog({ workspaceId, pageId, onOpenChange }: { workspaceId: string; pageId: string; onOpenChange: (o: boolean) => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [questions, setQuestions] = useState<{ type: LeadQuestionType; key: string; label: string; options: string }[]>([
    { type: "FULL_NAME", key: "", label: "", options: "" },
    { type: "EMAIL", key: "", label: "", options: "" },
    { type: "PHONE", key: "", label: "", options: "" },
  ]);
  const [privacy, setPrivacy] = useState("");
  const [thankTitle, setThankTitle] = useState("Obrigado!");
  const [thankBody, setThankBody] = useState("Recebemos seus dados e vamos falar com você em breve.");
  const [thankButton, setThankButton] = useState("Ver site");
  const [thankUrl, setThankUrl] = useState("");
  const create = useMutation({
    mutationFn: () => {
      const body: CreateLeadFormInput = {
        pageMetaId: pageId,
        name,
        questions: questions.map((q) =>
          q.type === "CUSTOM"
            ? { type: q.type, key: q.key, label: q.label, options: q.options ? q.options.split(",").map((s) => s.trim()).filter(Boolean) : undefined }
            : { type: q.type },
        ),
        privacyPolicyUrl: privacy,
        thankYou: { title: thankTitle, body: thankBody, buttonText: thankButton, buttonUrl: thankUrl },
      };
      return api7.createForm(workspaceId, body);
    },
    onSuccess: async (f: LeadFormDto) => {
      await queryClient.invalidateQueries({ queryKey: leadsKey(workspaceId) });
      toast.success(`Formulário "${f.name}" criado na página.`);
      onOpenChange(false);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <Dialog open={pageId.length > 0} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Criar formulário de lead</DialogTitle>
          <DialogDescription>Perguntas padrão (preenchidas pela Meta) e personalizadas, política de privacidade e tela de agradecimento.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <FormField id="f-nome" label="Nome do formulário"><Input id="f-nome" value={name} onChange={(e) => setName(e.target.value)} /></FormField>
          <div className="space-y-2">
            <p className="text-sm font-medium">Perguntas</p>
            {questions.map((q, i) => (
              <div key={i} className="grid gap-2 sm:grid-cols-[180px_1fr_1fr_1fr_auto]">
                <Select value={q.type} onValueChange={(v) => setQuestions(questions.map((x, j) => (j === i ? { ...x, type: v as LeadQuestionType } : x)))}>
                  <SelectTrigger aria-label="Tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEAD_QUESTION_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{LEAD_QUESTION_LABELS[t]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {q.type === "CUSTOM" ? (
                  <>
                    <Input placeholder="chave (ex.: tipo_de_obra)" value={q.key} onChange={(e) => setQuestions(questions.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))} />
                    <Input placeholder="Rótulo" value={q.label} onChange={(e) => setQuestions(questions.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
                    <Input placeholder="Opções separadas por vírgula (opcional)" value={q.options} onChange={(e) => setQuestions(questions.map((x, j) => (j === i ? { ...x, options: e.target.value } : x)))} />
                  </>
                ) : (
                  <span className="text-sm text-muted-foreground sm:col-span-3">preenchida automaticamente pela Meta</span>
                )}
                <Button variant="ghost" size="icon" aria-label="Remover" onClick={() => setQuestions(questions.filter((_, j) => j !== i))}><Trash2 /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setQuestions([...questions, { type: "CUSTOM", key: "", label: "", options: "" }])}><Plus /> Pergunta</Button>
          </div>
          <FormField id="f-priv" label="URL da política de privacidade"><Input id="f-priv" value={privacy} onChange={(e) => setPrivacy(e.target.value)} placeholder="https://…" /></FormField>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField id="f-tt" label="Agradecimento · título"><Input id="f-tt" value={thankTitle} onChange={(e) => setThankTitle(e.target.value)} /></FormField>
            <FormField id="f-tb" label="Agradecimento · botão"><Input id="f-tb" value={thankButton} onChange={(e) => setThankButton(e.target.value)} /></FormField>
            <FormField id="f-tbody" label="Agradecimento · mensagem"><Textarea id="f-tbody" value={thankBody} onChange={(e) => setThankBody(e.target.value)} /></FormField>
            <FormField id="f-turl" label="Agradecimento · URL do botão"><Input id="f-turl" value={thankUrl} onChange={(e) => setThankUrl(e.target.value)} placeholder="https://…" /></FormField>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => create.mutate()} loading={create.isPending}>Criar na Meta</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Destinos -------------------------------------------------------------------------------------

function DestinationsTab({ workspaceId, canWrite }: { workspaceId: string; canWrite: boolean }) {
  const queryClient = useQueryClient();
  const destinations = useQuery(queries7.leadDestinations(workspaceId));
  const overview = useQuery(queries7.leadsOverview(workspaceId));
  const clients = useQuery(queries.clients(workspaceId));
  const [novo, setNovo] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [remove, setRemove] = useState<LeadDestinationDto | null>(null);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: [...leadsKey(workspaceId), "destinations"] });
  const toggle = useMutation({
    mutationFn: (d: LeadDestinationDto) => api7.updateDestination(workspaceId, d.id, { active: !d.active }),
    onSuccess: () => invalidate(),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const del = useMutation({
    mutationFn: (id: string) => api7.deleteDestination(workspaceId, id),
    onSuccess: async () => {
      await invalidate();
      setRemove(null);
      toast.success("Destino excluído.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Cada lead novo é enviado aos destinos ativos que casam com o formulário e/ou cliente (sem nenhum dos dois = todos os leads). Falhas são reenviadas com backoff.</p>
        {canWrite ? <Button onClick={() => setNovo(true)}><Plus /> Novo destino</Button> : null}
      </div>
      <Card>
        <CardContent>
          {destinations.isPending ? (
            <LoadingState rows={2} />
          ) : destinations.isError ? (
            <ErrorState error={destinations.error} onRetry={() => void destinations.refetch()} />
          ) : destinations.data.length === 0 ? (
            <EmptyState icon={<Users className="size-8" aria-hidden />} title="Nenhum destino" description="Cadastre o webhook do CRM, e-mails da equipe, a planilha do Google ou a API de WhatsApp do cliente." />
          ) : (
            <ul className="divide-y">
              {destinations.data.map((d) => (
                <li key={d.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{d.name}</p>
                      <Badge variant="outline">{LEAD_DESTINATION_LABELS[d.kind]}</Badge>
                      <Badge variant={d.active ? "success" : "secondary"}>{d.active ? "ativo" : "pausado"}</Badge>
                      {d.lastDeliveryStatus ? <Badge variant={d.lastDeliveryStatus === "ok" ? "success" : d.lastDeliveryStatus === "erro" ? "destructive" : "warning"}>última: {d.lastDeliveryStatus}</Badge> : null}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {typeof d.config.url === "string" ? d.config.url : Array.isArray(d.config.emails) ? (d.config.emails as string[]).join(", ") : ""}
                      {d.clientId ? ` · cliente ${clients.data?.find((c) => c.id === d.clientId)?.name ?? ""}` : ""}
                      {d.formMetaId ? ` · formulário ${overview.data?.forms.find((f) => f.metaId === d.formMetaId)?.name ?? d.formMetaId}` : ""}
                    </p>
                  </div>
                  {canWrite ? (
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => toggle.mutate(d)}>{d.active ? "Pausar" : "Ativar"}</Button>
                      <Button variant="ghost" size="icon" aria-label="Excluir" onClick={() => setRemove(d)}><Trash2 /></Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <DestinationDialog
        workspaceId={workspaceId}
        open={novo}
        onOpenChange={setNovo}
        forms={overview.data?.forms ?? []}
        onCreated={(d) => {
          void invalidate();
          if (d.secret) setSecret(d.secret);
        }}
      />
      <Dialog open={secret !== null} onOpenChange={(o) => !o && setSecret(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Segredo do destino</DialogTitle>
            <DialogDescription>Guarde agora: não será mostrado de novo. Webhook: confere o HMAC do cabeçalho X-IAGENIX-Signature. Google Sheets: o Apps Script compara com o campo "secret" do corpo.</DialogDescription>
          </DialogHeader>
          <SecretBox secret={secret ?? ""} />
          <DialogFooter><Button onClick={() => setSecret(null)}>Entendi</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={remove !== null} onOpenChange={(o) => !o && setRemove(null)} title="Excluir o destino?" description="Os envios já feitos ficam no histórico dos leads." confirmLabel="Excluir" destructive loading={del.isPending} onConfirm={() => remove && del.mutate(remove.id)} />
    </div>
  );
}

function DestinationDialog({ workspaceId, open, onOpenChange, forms, onCreated }: { workspaceId: string; open: boolean; onOpenChange: (o: boolean) => void; forms: LeadFormDto[]; onCreated: (d: LeadDestinationDto & { secret?: string }) => void }) {
  const clients = useQuery(queries.clients(workspaceId));
  const [kind, setKind] = useState<LeadDestinationKind>("webhook");
  const [name, setName] = useState("");
  const [clientId, setClientId] = useState(ALL);
  const [formMetaId, setFormMetaId] = useState(ALL);
  const [url, setUrl] = useState("");
  const [emails, setEmails] = useState("");
  const [authHeader, setAuthHeader] = useState("Authorization");
  const [authValue, setAuthValue] = useState("");
  const [template, setTemplate] = useState("Olá {nome}! Recebemos seu contato pelo formulário {formulario}. Em breve falaremos com você.");
  const create = useMutation({
    mutationFn: () => {
      const config: CreateLeadDestinationInput["config"] =
        kind === "webhook"
          ? { kind, url }
          : kind === "email"
            ? { kind, emails: emails.split(",").map((s) => s.trim()).filter(Boolean) }
            : kind === "google_sheets"
              ? { kind, url }
              : { kind, url, authHeader: authHeader || undefined, authValue: authValue || undefined, messageTemplate: template };
      return api7.createDestination(workspaceId, { name, clientId: clientId === ALL ? null : clientId, formMetaId: formMetaId === ALL ? null : formMetaId, config });
    },
    onSuccess: (d) => {
      onOpenChange(false);
      setName("");
      setUrl("");
      setEmails("");
      onCreated(d);
      toast.success("Destino criado.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Novo destino</DialogTitle>
          <DialogDescription>Para onde mandar cada lead novo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <FormField id="d-kind" label="Tipo">
            <Select value={kind} onValueChange={(v) => setKind(v as LeadDestinationKind)}>
              <SelectTrigger id="d-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LEAD_DESTINATION_KINDS.map((k) => (
                  <SelectItem key={k} value={k}>{LEAD_DESTINATION_LABELS[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField id="d-nome" label="Nome"><Input id="d-nome" value={name} onChange={(e) => setName(e.target.value)} /></FormField>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField id="d-cliente" label="Cliente (opcional)">
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger id="d-cliente"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos</SelectItem>
                  {clients.data?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField id="d-form" label="Formulário (opcional)">
              <Select value={formMetaId} onValueChange={setFormMetaId}>
                <SelectTrigger id="d-form"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos</SelectItem>
                  {forms.map((f) => <SelectItem key={f.metaId} value={f.metaId}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
          </div>
          {kind === "email" ? (
            <FormField id="d-emails" label="E-mails (separados por vírgula)"><Input id="d-emails" value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="vendas@cliente.com, gerente@cliente.com" /></FormField>
          ) : (
            <FormField
              id="d-url"
              label={kind === "google_sheets" ? "URL do Web App do Apps Script (…/exec)" : kind === "whatsapp" ? "URL da API do provedor (Z-API, Evolution, Twilio…)" : "URL do webhook (https)"}
              hint={kind === "google_sheets" ? "Passo a passo do Apps Script no README (seção Leads → Google Sheets)." : kind === "whatsapp" ? "Recebe POST JSON { phone, message, lead }." : "Recebe POST JSON assinado (HMAC-SHA256 no X-IAGENIX-Signature)."}
            >
              <Input id="d-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" />
            </FormField>
          )}
          {kind === "whatsapp" ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField id="d-ah" label="Cabeçalho de autenticação"><Input id="d-ah" value={authHeader} onChange={(e) => setAuthHeader(e.target.value)} /></FormField>
                <FormField id="d-av" label="Valor (token do provedor)"><Input id="d-av" type="password" value={authValue} onChange={(e) => setAuthValue(e.target.value)} /></FormField>
              </div>
              <FormField id="d-tpl" label="Mensagem" hint="Variáveis: {nome}, {email}, {telefone}, {formulario}, {campanha}."><Textarea id="d-tpl" value={template} onChange={(e) => setTemplate(e.target.value)} /></FormField>
            </>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => create.mutate()} loading={create.isPending}>Criar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
